// Where an invoice stands with the person who approves it: sent to them, and
// approved by them. Two dated facts ABOUT the document, like the day it was
// paid, so they can be recorded on a draft and on an issued invoice alike.

import type { ReactNode } from "react";
import { InvoiceUpdate } from "@crm/shared";
import type { InvoiceCreate, InvoiceListItem } from "@crm/shared";
import { useInvoiceMutations } from "../../api/mutations.js";
import { FormDialog } from "../../forms/FormDialog.js";
import { DateField, TextField } from "../../forms/fields.js";
import { optionalDate, optionalText } from "../../forms/parseInput.js";
import { useEditor, type Wire } from "../../forms/useEditor.js";

interface ApprovalDraft {
  sent_for_approval_on: string;
  sent_for_approval_to: string;
  approved_on: string;
  approved_by: string;
}

const LABELS = {
  sent_for_approval_on: "Sent for approval on",
  sent_for_approval_to: "Sent to",
  approved_on: "Approved on",
  approved_by: "Approved by",
} as const;

const BLANK: ApprovalDraft = {
  sent_for_approval_on: "",
  sent_for_approval_to: "",
  approved_on: "",
  approved_by: "",
};

function draftFromRecord(invoice: InvoiceListItem): ApprovalDraft {
  return {
    sent_for_approval_on: invoice.sent_for_approval_on ?? "",
    sent_for_approval_to: invoice.sent_for_approval_to ?? "",
    approved_on: invoice.approved_on ?? "",
    approved_by: invoice.approved_by ?? "",
  };
}

// Blank is null: not sent, or not approved. A "who" goes with its date, so a
// cleared date clears it here as the server would.
function toWire(draft: ApprovalDraft): Wire {
  const sentOn = optionalDate(draft.sent_for_approval_on);
  const approvedOn = optionalDate(draft.approved_on);
  return {
    sent_for_approval_on: sentOn,
    sent_for_approval_to: sentOn === null ? null : optionalText(draft.sent_for_approval_to),
    approved_on: approvedOn,
    approved_by: approvedOn === null ? null : optionalText(draft.approved_by),
  };
}

export interface InvoiceApprovalDialogProps {
  invoice: InvoiceListItem;
  onClose: () => void;
}

export function InvoiceApprovalDialog({ invoice, onClose }: InvoiceApprovalDialogProps): ReactNode {
  const mutations = useInvoiceMutations();
  const editor = useEditor<ApprovalDraft, InvoiceCreate, InvoiceUpdate, InvoiceListItem>({
    mode: { kind: "update", record: invoice },
    blankDraft: BLANK,
    draftFromRecord,
    toWire,
    updateSchema: InvoiceUpdate,
    update: (id, body, version) => mutations.update.mutateAsync({ id, body, version }),
    fetchCurrent: mutations.fetchCurrent,
    onSaved: onClose,
  });

  return (
    <FormDialog
      open
      onClose={onClose}
      title="Invoice approval"
      description="Whether this invoice went to the person who approves it, and whether they approved. Nothing here changes what the invoice says or bills."
      submitLabel="Save changes"
      editor={editor}
      labels={LABELS}
    >
      <DateField
        label={LABELS.sent_for_approval_on}
        hint="Blank means it has not been sent for approval."
        value={editor.draft.sent_for_approval_on}
        error={editor.errors["sent_for_approval_on"]}
        onChange={(value) => editor.setField("sent_for_approval_on", value)}
      />
      <TextField
        label={LABELS.sent_for_approval_to}
        placeholder="Name or email"
        value={editor.draft.sent_for_approval_to}
        error={editor.errors["sent_for_approval_to"]}
        onChange={(value) => editor.setField("sent_for_approval_to", value)}
      />
      <DateField
        label={LABELS.approved_on}
        hint="Blank means it has not been approved."
        value={editor.draft.approved_on}
        error={editor.errors["approved_on"]}
        onChange={(value) => editor.setField("approved_on", value)}
      />
      <TextField
        label={LABELS.approved_by}
        placeholder="Name or email"
        value={editor.draft.approved_by}
        error={editor.errors["approved_by"]}
        onChange={(value) => editor.setField("approved_by", value)}
      />
    </FormDialog>
  );
}
